import React, { useMemo } from 'react';
import type { MgaPeriod } from '../useData';
import type { MgaRemittanceRow } from '../useData';
import { MgaReportRoot } from './MgaReportRoot';
import { ReportHeader } from './ReportHeader';
import { formatReportMoney, reportNum, reportRound2 } from './reportMeta';

export type BankCsvReportProps = {
  period: MgaPeriod;
  rows: MgaRemittanceRow[];
  companyLogoSrc?: string | null;
  companyLogoAlt?: string;
};

/** Preview of the fixed bank-upload columns (same as CSV download). */
export const BankCsvReport: React.FC<BankCsvReportProps> = ({
  period,
  rows,
  companyLogoSrc = null,
  companyLogoAlt,
}) => {
  const ccy = period.currency || 'USD';
  const preview = useMemo(() => {
    return rows.map((r) => {
      const valueDate = r.entryDate
        ? String(r.entryDate).slice(0, 10)
        : String(period.periodTo).slice(0, 10);
      const ref = `MGA-${period.id}-${(r.mgaCrewRequestId ?? '').toString() || '0'}`;
      return {
        beneficiary: r.allotteeName || r.crewName || '',
        bank: r.bank || '',
        account: r.bankAccount || '',
        amount: reportNum(r.amount),
        currency: (r.currency || ccy).toUpperCase(),
        valueDate,
        ref,
        crewName: r.crewName || '',
        rank: r.rankName || '',
        remarks: r.remarks || '',
      };
    });
  }, [rows, period, ccy]);

  const total = useMemo(
    () => reportRound2(preview.reduce((s, r) => s + r.amount, 0)),
    [preview],
  );

  return (
    <MgaReportRoot>
      <ReportHeader
        period={period}
        title="Allotment bank file"
        companyLogoSrc={companyLogoSrc}
        companyLogoAlt={companyLogoAlt}
      />
      <p className="mga-report-note">
        Fixed columns for bank upload: BeneficiaryName, BankName, AccountNumber, Amount, Currency,
        ValueDate, PaymentReference (+ CrewName, Rank, Remarks).
      </p>

      <section className="mga-report-section">
        <table className="mga-report-table">
          <thead>
            <tr>
              <th>Beneficiary</th>
              <th>Bank</th>
              <th>Account</th>
              <th className="mga-report-num">Amount</th>
              <th>Currency</th>
              <th>Value date</th>
              <th>Ref</th>
              <th>Crew</th>
              <th>Rank</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {preview.length === 0 ? (
              <tr>
                <td colSpan={10} className="mga-report-muted">
                  No bank rows
                </td>
              </tr>
            ) : (
              preview.map((r, i) => (
                <tr key={`${r.ref}-${i}`}>
                  <td>{r.beneficiary || '—'}</td>
                  <td>{r.bank || '—'}</td>
                  <td>{r.account || '—'}</td>
                  <td className="mga-report-num">{formatReportMoney(r.amount)}</td>
                  <td>{r.currency}</td>
                  <td>{r.valueDate}</td>
                  <td>{r.ref}</td>
                  <td>{r.crewName || '—'}</td>
                  <td>{r.rank || '—'}</td>
                  <td>{r.remarks || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
          {preview.length > 0 ? (
            <tfoot>
              <tr>
                <td colSpan={3} className="mga-report-emph">
                  Total ({preview.length})
                </td>
                <td className="mga-report-num mga-report-emph">{formatReportMoney(total)}</td>
                <td colSpan={6} />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </section>
    </MgaReportRoot>
  );
};

export default BankCsvReport;
